import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ReactGA from "react-ga4";
import { useGrowthBook } from "@growthbook/growthbook-react";

import {
  IS_DEV_ENV,
  LINKS,
  IS_GROWTHBOOK_ENABLED,
  LOCALSTORAGE_ITEMS,
} from "@constants";
import Router from "components/Router";
// FIXME error in import order if it's before Router
import { sendPromoCode, updateLocalStorage } from "actions/auth";
import { clearNewOriginalData } from "actions/recording";
import Loader from "components/common/Loader";
import localStorageService from "services/localStorage";
import { voicesByIds } from "actions/voice";
import {
  getDefaultAccentOrNarrationStyle,
  getNarrationStylesForSelectedLang,
} from "store/voice";
import { logger } from "utils/logger";

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const {
    isAuthenticated,
    loadingGlobal,
    isTrial,
    verified,
    user: { id },
    localStorage: ls,
    isPromoCodeResultReceived,
  } = useSelector((state) => state.auth);
  const { newOriginalData } = useSelector((state) => state.recording.originals);

  const websitePages = [LINKS.home, LINKS.faq, LINKS.pricing, LINKS.useCases];

  const isWebsitePages = websitePages.includes(path);

  const isPageToTrack = [...websitePages, LINKS.signup, LINKS.login].includes(
    path
  );

  const [preselectedVoiceId, setPreselectedVoiceId] = useState(null);
  const preselectedVoice = useSelector(
    (state) => state.voice.hash[preselectedVoiceId]
  );

  let gb;
  if (IS_GROWTHBOOK_ENABLED) {
    gb = useGrowthBook();
  }

  useEffect(() => {
    if (
      process.env.REACT_APP_USE_TRACKERS.toString() === "true" &&
      !isAuthenticated &&
      (isWebsitePages || isPageToTrack) &&
      !loadingGlobal
    ) {
      const initTrackers = async () => {
        try {
          const FBPixel = await (await import("react-facebook-pixel")).default;
          const RedditPixel = await (
            await import("react-reddit-pixel")
          ).default;
          FBPixel.init(
            process.env.REACT_APP_FACEBOOK_PIXEL_ID,
            {},
            {
              autoConfig: true,
              debug: IS_DEV_ENV,
            }
          );
          FBPixel.pageView();
          RedditPixel.init(process.env.REACT_APP_REDDIT_PIXEL_ID, {
            debug: IS_DEV_ENV,
          });
          RedditPixel.pageVisit();
          ReactGA.initialize([
            {
              trackingId: process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID,
              gaOptions: {
                testMode: IS_DEV_ENV,
              },
            },
            {
              trackingId: process.env.REACT_APP_GOOGLE_ADS_ID,
              gaOptions: {
                testMode: IS_DEV_ENV,
              },
            },
          ]);
          ReactGA.send({ hitType: "pageview" });
        } catch (e) {
          logger.error("Trackers init error", e);
        }
      };
      const _hsp = (window._hsp = window._hsp || []);
      _hsp.push([
        "addPrivacyConsentListener",
        (consent) => {
          if (consent.categories.analytics) {
            initTrackers();
          }
        },
      ]);
    }
  }, [isAuthenticated, isWebsitePages, loadingGlobal]);

  useEffect(() => {
    if (IS_GROWTHBOOK_ENABLED && id && gb && isAuthenticated) {
      gb.setAttributes({
        id,
      });
      gb.loadFeatures();
    }
  }, [isAuthenticated, id, gb]);

  useEffect(() => {
    const promo = localStorageService.getItem(LOCALSTORAGE_ITEMS.promo, true);
    if (isAuthenticated && verified && promo && Object.keys(promo).length > 0) {
      localStorageService.clearItem(LOCALSTORAGE_ITEMS.promo);
      dispatch(sendPromoCode({ promo_code: promo.code, place: promo.place }));
    }

    const voiceId = localStorageService.getItem(
      LOCALSTORAGE_ITEMS.preselectedVoiceId
    );
    if (isAuthenticated && verified && voiceId) {
      localStorageService.clearItem(LOCALSTORAGE_ITEMS.preselectedVoiceId);
      dispatch(voicesByIds([voiceId]));
      setPreselectedVoiceId(voiceId);
    }
  }, [isAuthenticated, verified, localStorageService, dispatch]);

  useEffect(() => {
    if (isPromoCodeResultReceived && !preselectedVoice) {
      navigate(LINKS.plan);
    }
  }, [isPromoCodeResultReceived]);

  useEffect(() => {
    if (preselectedVoice) {
      const f = async () => {
        const defaultAccent = getDefaultAccentOrNarrationStyle(
          preselectedVoice.accents
        );
        const defaultNarrationStyle = getDefaultAccentOrNarrationStyle(
          getNarrationStylesForSelectedLang(
            preselectedVoice.narrationStyles,
            null
          )
        );
        const voiceData = {
          id: preselectedVoice.id,
          name: preselectedVoice.name,
          thumbnail: preselectedVoice.thumbnail,
          pitch: 0,
          accent: defaultAccent?.id,
          accentName: defaultAccent?.info?.short_name,
          isDefault: defaultAccent?.is_default,
          narrationStyle: defaultNarrationStyle?.id,
          narrationStyleName: defaultNarrationStyle?.info?.name,
          isDefaultNarrationStyle: defaultNarrationStyle?.is_default,
          labelAccent: preselectedVoice.name,
          labelNarrationStyle: preselectedVoice.name,
        };
        await dispatch(
          updateLocalStorage({
            ...ls,
            [LOCALSTORAGE_ITEMS.lastUsedVoices]: {
              isVoiceTalent: true,
              [Date.now()]: voiceData,
            },
          })
        );
      };
      f();
    }
  }, [preselectedVoice]);

  useEffect(() => {
    if (
      newOriginalData?.projectId &&
      !newOriginalData?.ignoreClear &&
      ![
        LINKS.chooseVoices,
        LINKS.auditionVoices,
        LINKS.audioSettings,
        LINKS.calibrations,
        LINKS.createCalibration,
        LINKS.createRecording,
      ].includes(path)
    ) {
      localStorageService.setItem(
        LOCALSTORAGE_ITEMS.newOriginalData,
        undefined
      );
      dispatch(clearNewOriginalData());
    }
  }, [path, newOriginalData?.projectId, newOriginalData?.ignoreClear]);

  if (loadingGlobal) {
    return <Loader />;
  }

  return <Router />;
}

export default App;
